/**
 * MatterHealthCard - Visual health indicator for matters
 *
 * Shows at-a-glance health metrics:
 * - Days since last activity
 * - Outstanding tasks status
 * - Upcoming deadlines
 * - Communication recency
 *
 * Color coding:
 * - Green: Healthy (recent activity, tasks on track)
 * - Amber: Needs attention (stale, tasks overdue)
 * - Red: Critical (very stale, many overdue tasks)
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBar,
  Clock,
  ListChecks,
  CalendarBlank,
  Warning,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { fetchTasks } from '@/lib/tasksApi';
import { fetchHearings } from '@/lib/hearingsApi';
import { fetchMatterIntelligence } from '@/lib/mattersApi';

type HealthStatus = 'healthy' | 'attention' | 'critical';

interface MatterHealthCardProps {
  matterId: string;
  lastActivityDate?: number | string;
}

interface HealthMetric {
  label: string;
  value: string;
  status: HealthStatus;
  icon: React.ReactNode;
}

const STATUS_COLORS: Record<HealthStatus, { bg: string; text: string; icon: string }> = {
  healthy: { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-700', icon: 'text-emerald-600 dark:text-emerald-400' },
  attention: { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-700', icon: 'text-amber-600 dark:text-amber-400' },
  critical: { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-700', icon: 'text-red-600 dark:text-red-400' },
};

export function MatterHealthCard({ matterId, lastActivityDate }: MatterHealthCardProps) {
  const tasksQ = useQuery({
    queryKey: ['matter-tasks-health', matterId],
    queryFn: () => fetchTasks({ caseId: matterId, limit: 100 }),
    staleTime: 30_000,
  });

  // Fetch hearings directly — always in sync with the Hearings page
  const hearingsQ = useQuery({
    queryKey: ['matter-hearings-health', matterId],
    queryFn: fetchHearings,
    staleTime: 30_000,
  });

  // Canonical "last activity" — the SAME query the Intelligence panel uses (React Query dedupes,
  // so no extra request). Reading the one shared fact is what stops the two panels contradicting
  // each other ("Today" vs "501 days"). The prop is a fallback until the fact loads.
  const intelQ = useQuery({
    queryKey: ['matter-intelligence', matterId],
    queryFn: () => fetchMatterIntelligence(matterId),
    staleTime: 60_000,
    enabled: !!matterId,
  });
  const canonicalLastActivity = intelQ.data?.dormancy?.lastActivityAt ?? lastActivityDate;

  const metrics = useMemo<HealthMetric[]>(() => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const result: HealthMetric[] = [];

    // 1. Days since last activity (canonical fact)
    if (canonicalLastActivity) {
      const lastDate = new Date(canonicalLastActivity);
      const daysSince = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
      let activityStatus: HealthStatus = 'healthy';
      if (daysSince > 14) activityStatus = 'critical';
      else if (daysSince > 7) activityStatus = 'attention';

      result.push({
        label: 'Last activity',
        value: daysSince === 0 ? 'Today' : daysSince === 1 ? 'Yesterday' : `${daysSince} days ago`,
        status: activityStatus,
        icon: <Clock className="h-4 w-4" />,
      });
    }

    // 2. Tasks status
    const tasks = tasksQ.data?.tasks ?? [];
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((t) => t.status === 'completed').length;
    const overdueTasks = tasks.filter((t) => {
      if (t.status === 'completed') return false;
      if (!t.dueDate) return false;
      return new Date(t.dueDate) < now;
    }).length;

    let taskStatus: HealthStatus = 'healthy';
    if (overdueTasks > 2) taskStatus = 'critical';
    else if (overdueTasks > 0) taskStatus = 'attention';

    result.push({
      label: 'Tasks',
      value: overdueTasks > 0
        ? `${overdueTasks} overdue`
        : totalTasks > 0
          ? `${completedTasks}/${totalTasks} done`
          : 'No tasks',
      status: taskStatus,
      icon: <ListChecks className="h-4 w-4" />,
    });

    // 3. Next hearing — derived from live hearings data filtered to this matter
    const allHearings = hearingsQ.data ?? [];
    const nextHearing = allHearings
      .filter((h) => h.matterId === matterId && h.date >= todayStr && h.status !== 'vacated' && h.status !== 'disposed')
      .sort((a, b) => a.date.localeCompare(b.date))[0];

    if (nextHearing) {
      const hearingDate = new Date(nextHearing.date + 'T00:00:00');
      const daysUntil = Math.floor((hearingDate.getTime() - new Date(todayStr + 'T00:00:00').getTime()) / 86_400_000);
      let deadlineStatus: HealthStatus = 'healthy';
      if (daysUntil < 3) deadlineStatus = 'critical';
      else if (daysUntil < 7) deadlineStatus = 'attention';

      result.push({
        label: 'Next hearing',
        value: daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`,
        status: deadlineStatus,
        icon: <CalendarBlank className="h-4 w-4" />,
      });
    } else {
      result.push({
        label: 'Next hearing',
        value: 'None listed',
        status: 'healthy',
        icon: <CalendarBlank className="h-4 w-4" />,
      });
    }

    // 4. Pending urgent/high priority tasks
    const pendingHighPriority = tasks.filter((t) =>
      t.status !== 'completed' && (t.priority === 'urgent' || t.priority === 'high')
    ).length;
    let priorityStatus: HealthStatus = 'healthy';
    if (pendingHighPriority > 2) priorityStatus = 'critical';
    else if (pendingHighPriority > 0) priorityStatus = 'attention';

    result.push({
      label: 'Urgent items',
      value: pendingHighPriority > 0 ? `${pendingHighPriority} pending` : 'None',
      status: priorityStatus,
      icon: <Warning className="h-4 w-4" />,
    });

    return result;
  }, [canonicalLastActivity, matterId, tasksQ.data, hearingsQ.data]);

  // Calculate overall health
  const overallHealth = useMemo<HealthStatus>(() => {
    const criticalCount = metrics.filter((m) => m.status === 'critical').length;
    const attentionCount = metrics.filter((m) => m.status === 'attention').length;
    if (criticalCount > 0) return 'critical';
    if (attentionCount > 1) return 'attention';
    return 'healthy';
  }, [metrics]);

  const overallColors = STATUS_COLORS[overallHealth];
  const OverallIcon = overallHealth === 'healthy'
    ? CheckCircle
    : overallHealth === 'attention'
      ? Warning
      : XCircle;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ChartBar className="h-4 w-4" />
            Matter Health
          </CardTitle>
          <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 ${overallColors.bg}`}>
            <OverallIcon className={`h-3.5 w-3.5 ${overallColors.icon}`} weight="fill" />
            <span className={`text-xs font-semibold capitalize ${overallColors.text}`}>
              {overallHealth === 'healthy' ? 'On Track' : overallHealth === 'attention' ? 'Needs Attention' : 'Action Required'}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {metrics.map((metric) => {
            const colors = STATUS_COLORS[metric.status];
            return (
              <div
                key={metric.label}
                className={`rounded-lg border px-3 py-2.5 ${colors.bg} border-transparent`}
              >
                <div className="flex items-center gap-2">
                  <span className={colors.icon}>{metric.icon}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {metric.label}
                  </span>
                </div>
                <div className={`mt-1 text-sm font-semibold ${colors.text}`}>
                  {metric.value}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
